import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  RdsAutocompleteComponent,
  RdsFormFieldComponent,
  RdsLabelDirective,
} from '@rds/angular-components';
import { map } from 'rxjs';

import { AuthService } from '../../../auth';
import { UserResult } from '../../model/user-management.model';
import { UserManagementService } from '../../services/user-management.service';

@Component({
  selector: 'bsh-user-search-autocomplete',
  imports: [
    RdsAutocompleteComponent,
    ReactiveFormsModule,
    FormsModule,
    RdsFormFieldComponent,
    RdsLabelDirective,
  ],
  providers: [
    UserManagementService,
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: UserSearchAutocompleteComponent,
    },
  ],
  templateUrl: './user-search-autocomplete.component.html',
  styleUrl: './user-search-autocomplete.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchAutocompleteComponent implements ControlValueAccessor {
  readonly #userManagementService = inject(UserManagementService);
  readonly #authService = inject(AuthService);
  protected data = (query: string) => {
    return this.#userManagementService.loadUsers(query).pipe(
      map(users => {
        if (this.excludeCurrentUser) {
          const currentEmail = this.#authService.user?.email || '';
          if (currentEmail) {
            return users.filter(u => u.email !== currentEmail);
          }
        }
        return users;
      })
    );
  };

  @Input() config: { placeholder: string; label: string } = {
    placeholder: 'Search User...',
    label: 'Email',
  };

  @Input() minSearchChars: number = 0;

  @Input() excludeCurrentUser: boolean = false;

  protected user: UserResult | null = null;
  protected disabled = false;

  protected displayProperty(i: UserResult): string {
    return `${i.email}${i.fullName ? ` - (${i.fullName})` : ``}`;
  }
  protected uniqProperty(i: UserResult): string {
    return i.email;
  }

  protected propagateTouched() {}

  // eslint-disable-next-line
  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  // eslint-disable-next-line
  registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  writeValue(value: string): void {
    if (value == null) {
      this.user = null;
      return;
    }
    this.user = {
      email: value,
      rocheLoginId: '',
      photo: '',
      fullName: '',
      givenName: '',
      familyName: '',
      rochePersistentId: '',
      costCenter: '',
      department: '',
    } as UserResult;
  }

  onInputChange(value: UserResult) {
    const email = value ? value.email : null;
    this.propagateChange(email);
    this.propagateTouched();
  }

  // eslint-disable-next-line
  private propagateChange(_value: string | null) {}
}
